import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useData, useList } from '@yingbaba/api';
import {
  ButtonLink,
  SectionHead,
  ProviderCard,
  DemandCard,
  CaseCard,
  QueryState,
  Image,
  imageAssets,
} from '@yingbaba/ui';
import {
  Search,
  ArrowRight,
  ArrowUpRight,
  Clapperboard,
  Users,
  Camera,
  Sparkles,
  ShieldCheck,
  Check,
  MapPin,
  Mic,
  PawPrint,
  Lightbulb,
  Scissors,
  Palette,
  Film,
  FileText,
  Megaphone,
} from '@yingbaba/icons';
import type { Provider, Demand, Portfolio, CMSBlock } from '@yingbaba/types';
import { siteURL, money, date } from '@yingbaba/utils';
const entries = [
  { icon: FileText, title: '项目协作', sub: '创作需求 · 团队 · 任务 · 素材', href: '/projects' },
  {
    icon: Film,
    title: '寻找数字素材',
    sub: '视频 · 图片 · 音乐 · 制作模板',
    href: '/digital-resources',
  },
  { icon: Users, title: '发现创作团队', sub: '从作品认识专业与风格', href: '/providers' },
  { icon: Clapperboard, title: '发布拍片需求', sub: '说明目标 · 寻找合适团队', href: '/publish' },
];
const resourceIcons = [
  MapPin,
  Users,
  PawPrint,
  FileText,
  Camera,
  Clapperboard,
  Film,
  Palette,
  Mic,
  Scissors,
  Lightbulb,
];
const resourceNames = [
  '场景',
  '模特演员',
  '宠物',
  '文案策划',
  '摄影师',
  '导演',
  '编导',
  '美术指导',
  '配音',
  '剪辑师',
  '器材',
];

type MarketTrend = {
  category: string;
  demand_count: number;
  provider_count: number;
  demand_per_provider_tenths: number;
};
type MarketplaceOverview = {
  active_demands: number;
  new_demands_30_days: number;
  completed_projects_30_days: number;
  reviews_30_days: number;
  published_cases_30_days: number;
  trends: MarketTrend[];
};
type MarketplaceActivity = {
  type: string;
  title: string;
  subtitle: string;
  path: string;
  occurred_at: string;
};
type MarketplaceDiscovery = {
  mode: 'featured' | 'personalized';
  signals: string[];
  providers: Provider[];
  demands: Demand[];
  cases: Portfolio[];
  following: MarketplaceActivity[];
};
const searchKinds = [
  { kind: 'digital-resources', label: '数字素材', placeholder: '搜索视频、音乐、模板、标签或软件' },
  {
    kind: 'providers',
    label: '创作团队',
    placeholder: '搜索团队、创作方向，例如产品广告、动画制作',
  },
  { kind: 'resources', label: '影视资源', placeholder: '搜索场地、演员、摄影师或器材' },
  { kind: 'cases', label: '作品案例', placeholder: '搜索作品、行业或影像风格' },
  { kind: 'demands', label: '拍片需求', placeholder: '搜索项目、品牌或行业，发现新合作' },
];
export function Home() {
  const navigate = useNavigate();
  const [searchKind, setSearchKind] = useState('digital-resources');
  const currentSearch = searchKinds.find((item) => item.kind === searchKind)!;
  const popularTerms =
    searchKind === 'digital-resources'
      ? ['城市航拍', '背景音乐', '片头模板', '产品展示']
      : searchKind === 'resources'
        ? ['场景', '摄影师', '演员', '器材']
        : ['品牌宣传片', '产品广告', '短视频', '动画制作', '后期制作'];

  const providers = useList<Provider>('/providers?page_size=3'),
    demands = useList<Demand>('/demands?page_size=3'),
    cases = useList<Portfolio>('/cases?page_size=3');
  const overview = useData<MarketplaceOverview>('/marketplace/overview');
  const discovery = useData<MarketplaceDiscovery>('/marketplace/discovery');
  // Older API responses encode empty Go slices as null. Public browsing must
  // keep working across deployments and while the market has no activity.
  const trends = overview.data?.trends ?? [];
  const following = discovery.data?.following ?? [];
  const signals = discovery.data?.signals ?? [];
  const announcements = useList<CMSBlock>('/cms');
  const announcement = announcements.data?.items.find((block) => block.key === 'home.announcement');
  const shownProviders = discovery.data?.providers?.length
    ? discovery.data.providers.slice(0, 3)
    : providers.data?.items;
  const shownCases = discovery.data?.cases?.length
    ? discovery.data.cases.slice(0, 3)
    : cases.data?.items;
  const shownDemands = discovery.data?.demands?.length
    ? discovery.data.demands.slice(0, 3)
    : demands.data?.items;
  const showCases = cases.isPending || !!cases.error || !!shownCases?.length;
  const showProviders = providers.isPending || !!providers.error || !!shownProviders?.length;
  const showDemands = demands.isPending || !!demands.error || !!shownDemands?.length;
  return (
    <>
      {announcement && (
        <aside className="site-announcement">
          <div className="container row spread wrap">
            <div>
              <strong>{announcement.title}</strong>
              <span>{announcement.content}</span>
            </div>
            {announcement.link && (
              <a href={announcement.link}>
                了解详情 <ArrowUpRight size={14} />
              </a>
            )}
          </div>
        </aside>
      )}
      <section className="hero marketplace-hero">
        <picture>
          <source media="(max-width:767px)" srcSet={imageAssets.heroSmall} />
          <img
            className="hero-photo"
            src={imageAssets.hero}
            alt="影视创作中的分镜策划与拍摄准备"
            fetchPriority="high"
          />
        </picture>
        <div className="hero-shade" />
        <div className="container hero-content">
          <div className="hero-kicker">影爸爸 · 连接每一份创意与专业</div>
          <h1>
            让好创意，
            <span className="hero-title-result">
              拍成<em>好作品。</em>
            </span>
          </h1>
          <p>找素材、找团队、看作品，让创作从这里开始。</p>
          <div className="marketplace-search">
            <div className="marketplace-search-tabs" role="group" aria-label="搜索内容类型">
              {searchKinds.map((item) => (
                <button
                  type="button"
                  key={item.kind}
                  aria-pressed={searchKind === item.kind}
                  className={searchKind === item.kind ? 'active' : ''}
                  onClick={() => setSearchKind(item.kind)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <form
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                const q = String(new FormData(event.currentTarget).get('q') || '').trim();
                navigate(`/${searchKind}${q ? `?q=${encodeURIComponent(q)}` : ''}`);
              }}
            >
              <Search size={22} aria-hidden="true" />
              <input
                name="q"
                aria-label="搜索合作内容"
                placeholder={currentSearch.placeholder}
                maxLength={200}
              />
              <button type="submit" className="btn btn-primary">
                搜索 <ArrowRight size={17} />
              </button>
            </form>
          </div>
          <div className="hero-keywords">
            <span>热门方向</span>
            {popularTerms.map((category) => (
              <Link
                key={category}
                to={`/${searchKind}?${['providers', 'demands', 'cases'].includes(searchKind) ? 'category' : 'q'}=${encodeURIComponent(category)}`}
              >
                {category}
              </Link>
            ))}
          </div>
          <div className="hero-cooperate">
            <ButtonLink to="/projects" variant="accent">
              开始项目协作 <ArrowUpRight size={16} />
            </ButtonLink>
            <ButtonLink to="/digital-resources" variant="secondary">
              寻找数字素材 <ArrowRight size={16} />
            </ButtonLink>
          </div>
        </div>
      </section>
      <div style={{ background: '#fff' }}>
        <div className="container entry-strip">
          {entries.map((e) => (
            <Link key={e.title} to={e.href} className="entry-item">
              <span className="icon-tile">
                <e.icon size={24} strokeWidth={1.4} />
              </span>
              <div>
                <h3>{e.title}</h3>
                <p>{e.sub}</p>
              </div>
              <ArrowUpRight size={17} className="arrow" />
            </Link>
          ))}
        </div>
      </div>
      <section className="market-pulse-section">
        <div className="container market-pulse">
          <div className="market-pulse-copy">
            <span className="eyebrow">LIVE OPPORTUNITIES</span>
            <h2>市场正在发生</h2>
            <p>了解正在公开招募团队的项目，看看预算与制作方向。</p>
            {overview.data && (
              <div className="market-pulse-stats">
                <span>
                  <b>{overview.data.new_demands_30_days}</b>近 30 天新需求
                </span>
                <span>
                  <b>{overview.data.active_demands}</b>公开需求
                </span>
                <span>
                  <b>{overview.data.published_cases_30_days}</b>近 30 天新案例
                </span>
              </div>
            )}
          </div>
          <QueryState query={demands}>
            {demands.data?.items.length ? (
              <div className="market-pulse-list">
                {demands.data?.items.map((demand) => (
                  <Link to={`/demands/${demand.id}`} className="market-pulse-item" key={demand.id}>
                    <div>
                      <strong>{demand.title}</strong>
                      <span>
                        {demand.city} · {date(demand.deadline)}交付
                      </span>
                    </div>
                    <div className="market-pulse-value">
                      <b>
                        {money(demand.budget_min_minor)}–{money(demand.budget_max_minor)}
                      </b>
                      <span>{demand.candidate_count || 0} 家团队参与</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="market-pulse-empty">
                <h3>让下一份创作，从你的想法开始</h3>
                <p>新的合作需求会在这里出现。你也可以先建立项目，整理创作计划与素材。</p>
                <ButtonLink to="/projects" variant="secondary">
                  开始项目协作 <ArrowRight size={16} />
                </ButtonLink>
              </div>
            )}
          </QueryState>
        </div>
      </section>
      {Boolean(trends.length || following.length || signals.length) && (
        <section className="market-intelligence-section">
          <div className="container market-intelligence-grid">
            {trends.length ? (
              <div className="market-intelligence-panel">
                <div className="market-intelligence-head">
                  <div>
                    <span className="eyebrow">SUPPLY & DEMAND</span>
                    <h3>近 90 天供需趋势</h3>
                  </div>
                  <span className="muted">仅统计公开需求与认证团队</span>
                </div>
                <div className="trend-list">
                  {trends.slice(0, 4).map((trend) => (
                    <Link
                      key={trend.category}
                      className="trend-row"
                      to={`/demands?category=${encodeURIComponent(trend.category)}`}
                    >
                      <strong>{trend.category}</strong>
                      <span>{trend.demand_count} 个需求</span>
                      <span>{trend.provider_count} 家团队</span>
                      <b>
                        {trend.provider_count > 0
                          ? `需求 / 团队 ${(trend.demand_per_provider_tenths / 10).toFixed(1)}`
                          : '暂无认证团队'}
                      </b>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="market-intelligence-panel personalized-panel">
              {following.length ? (
                <>
                  <div className="market-intelligence-head">
                    <div>
                      <span className="eyebrow">FOLLOWING</span>
                      <h3>你关注的团队有新动态</h3>
                    </div>
                  </div>
                  <div className="following-activity">
                    {following.slice(0, 4).map((item, index) => (
                      <Link
                        className="following-item"
                        to={item.path}
                        key={`${item.type}-${item.path}-${index}`}
                      >
                        <div>
                          <strong>{item.title}</strong>
                          <span>{item.subtitle}</span>
                        </div>
                        <time>{date(item.occurred_at)}</time>
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="market-intelligence-head">
                    <div>
                      <span className="eyebrow">DISCOVERY</span>
                      <h3>
                        {discovery.data?.mode === 'personalized'
                          ? '更贴近你的发现结果'
                          : '从公开市场发现合适内容'}
                      </h3>
                    </div>
                  </div>
                  <p className="personalized-copy">
                    {discovery.data?.mode === 'personalized'
                      ? '推荐只参考你自己的需求、收藏、关注与公开分类信息，不使用隐藏的商业竞价权重。'
                      : '登录后，收藏案例、关注团队或发布需求，发现页会逐步根据这些明确行为调整内容顺序。'}
                  </p>
                  {!!signals.length && (
                    <div className="discovery-signals">
                      {signals.map((signal) => (
                        <span key={signal}>{signal}</span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}
      {showCases && (
        <section className="section home-cases">
          <div className="container">
            <SectionHead
              eyebrow="DISCOVER THE WORK"
              title="先看作品，再找到对的人"
              description="从真实案例认识创作风格、制作团队和专业方向；喜欢的效果，可以直接引用为下一份需求的参考。"
              to="/cases"
              linkLabel="探索精选案例"
            />
            <QueryState query={cases}>
              <div className="grid-3">
                {shownCases?.map((v) => (
                  <CaseCard key={v.id} item={v} />
                ))}
              </div>
            </QueryState>
          </div>
        </section>
      )}
      {showProviders && (
        <section className="section home-services">
          <div className="container">
            <SectionHead
              eyebrow="FIND YOUR CREATIVE PARTNER"
              title="好作品，从找到对的人开始"
              description="用作品了解风格，用沟通确认默契。发现适合你的创作团队。"
              to="/providers"
              linkLabel="探索全部团队"
            />
            <div className="service-tabs">
              {['全部团队', '品牌宣传片', '产品广告', '纪录片', '短视频'].map((v, i) => (
                <Link
                  key={v}
                  to={'/providers' + (i ? '?category=' + encodeURIComponent(v) : '')}
                  className={i === 0 ? 'link' : 'muted'}
                >
                  {v}
                </Link>
              ))}
            </div>
            <QueryState query={providers}>
              <div className="grid-3">
                {shownProviders?.map((v) => (
                  <ProviderCard key={v.id} item={v} />
                ))}
              </div>
            </QueryState>
          </div>
        </section>
      )}
      {showDemands && (
        <section className="section">
          <div className="container">
            <SectionHead
              eyebrow="OPPORTUNITIES, WORTH CREATING"
              title="新的故事，等你开拍"
              description="发现正在寻找创作伙伴的项目，让下一次合作从这里发生。"
              to="/demands"
              linkLabel="进入需求大厅"
            />
            <QueryState query={demands}>
              <div className="grid-3">
                {shownDemands?.map((v) => (
                  <DemandCard key={v.id} item={v} />
                ))}
              </div>
            </QueryState>
          </div>
        </section>
      )}
      <section className="section home-resources" style={{ background: '#edf0e8' }}>
        <div className="container">
          <SectionHead
            eyebrow="EVERY DETAIL, ALL TOGETHER"
            title="一部好片，需要的都在这里"
            description="从第一束光到最后一帧画面，连接影视制作的每个环节。"
            to="/resources"
            linkLabel="探索资源市场"
          />
          <div className="resource-links">
            {resourceNames.map((name, i) => {
              const I = resourceIcons[i];
              return (
                <Link
                  key={name}
                  className="resource-link"
                  to={'/resources?category=' + encodeURIComponent(name)}
                >
                  <I size={21} strokeWidth={1.5} />
                  {name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <SectionHead
            eyebrow="YOUR IDEAS, ONE STEP FURTHER"
            title="灵感明确以后，让方案更快成形"
            description="用 AI 梳理需求、构思脚本与分镜，把脑海里的画面变成能沟通、能修改的创作方案。"
            to="/ai"
            linkLabel="探索 AI 创作"
          />
          <div className="feature-banner">
            <Image src={imageAssets.post} alt="影视剪辑与调色工作室" />
            <div className="feature-copy">
              <span className="eyebrow">YOUR IDEAS, ONE STEP FURTHER</span>
              <h2>灵感不必等，创作再向前一步。</h2>
              <p>
                用 AI 梳理需求、构思脚本与分镜。
                <br />
                把脑海里的画面，变成能沟通、能修改的创作方案。
              </p>
              <ButtonLink to="/ai" variant="accent">
                探索 AI 创作
                <Sparkles size={16} />
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <SectionHead
            eyebrow="FROM AN IDEA TO A FILM"
            title="合作有章法，创作更从容"
            description="把复杂的制作流程，变成清晰的每一步。"
          />
          <div className="process">
            {[
              ['说说你的想法', '填写类型、预算与时间，让创作团队更快理解你的期待。'],
              ['找到合拍的团队', '了解团队与作品，沟通细节，自主选择合作伙伴。'],
              ['每一步，都有记录', '会议、脚本、进度和文件集中管理，让协作更透明。'],
              ['让作品继续发光', '整理作品，规划推广，让好内容走得更远。'],
            ].map(([t, d], i) => (
              <div className="process-step" key={t}>
                <div className="number">0{i + 1} /</div>
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="section container">
        <div className="bottom-cta">
          <div>
            <h2>你的下一个好作品，从这里开始。</h2>
            <p>有想法，就迈出第一步。让影爸爸陪你把它实现。</p>
          </div>
          <ButtonLink to="/publish">
            免费发布需求
            <ArrowUpRight size={17} />
          </ButtonLink>
        </div>
      </section>
    </>
  );
}
